/**
 * CustomerDealsTab - Aba de negócios do modal premium de cliente
 * Exibe lista de deals em formato de tabela premium com lazy loading
 * Inclui ordenação, filtros por status e coluna de vendedor
 * 
 * IMPORTANTE: Usa tabela nativa HTML (<table>) ao invés do componente Table
 * para evitar conflito de overflow com o wrapper automático do shadcn Table.
 * Isso garante scroll horizontal funcional dentro do modal.
 * 
 * @version 3.6.0
 * RELEASE: 2026-10-18 v3.6.0 - Botão "Novo Negócio" sempre visível no cabeçalho; na ficha do
 *          veículo o formulário já abre com o contato e o veículo selecionados
 * RELEASE: 2026-08-07 v3.3.0 - Botão "Novo Negócio" no estado vazio (gated por permissão de criação)
 * RELEASE: 2026-01-10 - Fix scroll horizontal com tabela nativa
 */
import React, { useState, useMemo, useRef, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { 
  Briefcase, 
  ExternalLink,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  Plus
} from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { DealFormDialog } from '@/components/crm/deals/DealFormDialog';
import { useCustomerDeals, CustomerDeal } from '@/hooks/useCustomerDeals';
import { DealStatusBadgePremium } from '@/components/crm/deals/modal/components/DealStatusBadgePremium';
import { DealPriorityBadgePremium } from '@/components/crm/deals/modal/components/DealPriorityBadgePremium';
import { DealPhaseProgress } from '@/components/crm/deals/DealPhaseProgress';
import { PriceDisplay } from '@/components/crm/PriceDisplay';
import { SmartAvatar } from '@/components/ui/smart-avatar';
import { formatDealCode } from '@/lib/formatters';
import { usePipelinePhaseStats } from '@/hooks/usePipelinePhaseStats';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
// NOTA: Usando TableBody/TableCell/TableHead/TableHeader/TableRow mas NÃO o Table wrapper
// O Table wrapper injeta overflow-auto que conflita com nosso controle externo
import {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { createLogger } from '@/lib/logger';

// Logger para debug de scroll (apenas em DEV)
const logger = createLogger('CustomerDealsTab');

// ============================================================================
// TIPOS
// ============================================================================

/** Props do componente principal */
interface CustomerDealsTabProps {
  /** ID do cliente (null no modo Contato — busca por contactId) */
  customerId: string | null;
  contactId?: string | null;
  /** v3.5.0: contexto de veículo (aba Negócios do modal de veículo) */
  vehicleId?: string | null;
  /** v3.6.0: rótulo do veículo para pré-selecionar no novo negócio */
  vehicleLabel?: string | null;
  /** v3.6.0: contato pré-selecionado no novo negócio (não altera a busca) */
  newDealContactId?: string | null;
  isActive?: boolean;
  onOpenDeal?: (dealId: string) => void;
}

/** Campos disponíveis para ordenação */
type SortField = 'title' | 'value' | 'status' | 'created_at' | 'expected_close_date';

/** Direção da ordenação */
type SortDirection = 'asc' | 'desc';

/** Filtro por status */
type StatusFilter = 'all' | 'open' | 'won' | 'lost';

// ============================================================================
// COMPONENTE: HEADER ORDENÁVEL
// ============================================================================

/**
 * Header de coluna com suporte a ordenação
 * Exibe ícone indicando estado atual da ordenação
 */
function SortableHeader({ 
  field, 
  label,
  currentField,
  direction,
  onSort,
  align = 'left'
}: {
  field: SortField;
  label: string;
  currentField: SortField;
  direction: SortDirection;
  onSort: (field: SortField) => void;
  align?: 'left' | 'right' | 'center';
}) {
  const isActive = currentField === field;
  
  return (
    <TableHead 
      className={cn(
        "text-xs font-medium py-2 cursor-pointer hover:bg-muted/30 transition-colors select-none",
        align === 'right' && 'text-right pr-4',
        align === 'center' && 'text-center'
      )}
      onClick={() => onSort(field)}
    >
      <div className={cn(
        "flex items-center gap-1",
        align === 'right' && 'justify-end',
        align === 'center' && 'justify-center'
      )}>
        <span>{label}</span>
        {isActive ? (
          direction === 'asc' 
            ? <ChevronUp className="h-3 w-3" />
            : <ChevronDown className="h-3 w-3" />
        ) : (
          <ArrowUpDown className="h-3 w-3 opacity-30" />
        )}
      </div>
    </TableHead>
  );
}

// ============================================================================
// COMPONENTE: CÉLULA DE PROGRESSO DA FASE
// ============================================================================

/**
 * Componente auxiliar para exibir progresso da fase
 * Busca total de fases do pipeline para calcular progresso
 * Usa DealPhaseProgress para manter padrão visual unificado
 */
function PhaseProgressCell({ deal }: { deal: CustomerDeal }) {
  const pipelineId = deal.pipeline?.id || null;
  const { phases } = usePipelinePhaseStats(pipelineId);
  const totalPhases = phases.length || 1;
  
  if (!deal.phase) {
    return <span className="text-xs text-muted-foreground">-</span>;
  }
  
  return (
    <DealPhaseProgress
      phaseName={deal.phase.name}
      phaseColor={deal.phase.color}
      phaseSortOrder={deal.phase.sort_order || 1}
      totalPhases={totalPhases}
    />
  );
}

// ============================================================================
// UTILITÁRIO: COR DA BARRA LATERAL POR STATUS DO DEAL
// ============================================================================

/**
 * Retorna a classe de cor para a barra lateral baseado no status do deal
 * Segue padrão visual da listagem principal de negócios
 */
function getDealStatusBarColor(status: string): string {
  switch (status) {
    case 'open': return 'bg-blue-500';
    case 'won': return 'bg-green-500';
    case 'lost': return 'bg-red-500';
    case 'abandoned': return 'bg-muted-foreground/40';
    default: return 'bg-muted-foreground/40';
  }
}

// ============================================================================
// COMPONENTE: LINHA DA TABELA
// ============================================================================

/**
 * Linha da tabela de deal - layout premium
 * Inclui workspace, vendedor, datas de criação e fechamento
 */
function DealTableRow({ 
  deal, 
  onOpen 
}: {
  deal: CustomerDeal; 
  onOpen?: () => void;
}) {
  return (
    <TableRow 
      className="cursor-pointer hover:bg-muted/50 group border-b border-border/40"
      onClick={onOpen}
    >
      {/* Barra lateral colorida por status */}
      <TableCell className="p-0 w-2">
        <div className={cn('w-1 h-full min-h-[40px] rounded-full', getDealStatusBarColor(deal.status))} />
      </TableCell>

      {/* N.Negócio - ID formatado (primeiros 8 chars) */}
      <TableCell className="px-2 py-1.5">
        <span className="text-xs font-mono text-muted-foreground bg-muted/50 px-1.5 py-0.5 rounded">
          {formatDealCode(deal.id)}
        </span>
      </TableCell>

      {/* Título do negócio */}
      <TableCell className="font-medium max-w-[120px] px-2 py-1.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="truncate block text-sm">
              {deal.title}
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-[300px]">
            <p className="text-xs">{deal.title}</p>
          </TooltipContent>
        </Tooltip>
      </TableCell>

      {/* Status */}
      <TableCell className="px-2 py-1.5">
        <DealStatusBadgePremium status={deal.status} size="xs" />
      </TableCell>

      {/* Prioridade */}
      <TableCell className="px-2 py-1.5">
        <DealPriorityBadgePremium priority={deal.priority} size="xs" variant="inline" />
      </TableCell>

      {/* Vendedor Responsável */}
      <TableCell className="px-2 py-1.5">
        {deal.seller ? (
          <div className="flex items-center gap-1">
            <SmartAvatar 
              src={deal.seller.avatar_url}
              name={deal.seller.name}
              size="xs"
              use3DFallback
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-muted-foreground truncate max-w-[45px]">
                  {deal.seller.name}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="text-xs">{deal.seller.name}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        )}
      </TableCell>

      {/* Workspace */}
      <TableCell className="px-2 py-1.5">
        {deal.pipeline?.workspace ? (
          <div className="flex items-center gap-1">
            <div 
              className="w-2 h-2 rounded-sm shrink-0"
              style={{ backgroundColor: deal.pipeline.workspace.color }}
            />
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="text-xs text-muted-foreground truncate max-w-[50px]">
                  {deal.pipeline.workspace.name}
                </span>
              </TooltipTrigger>
              <TooltipContent side="top">
                <p className="text-xs">{deal.pipeline.workspace.name}</p>
              </TooltipContent>
            </Tooltip>
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">-</span>
        )}
      </TableCell>

      {/* Pipeline */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
        {deal.pipeline?.name || '-'}
      </TableCell>

      {/* Fase com progresso - padrão unificado com listagem */}
      <TableCell className="px-2 py-1.5">
        <PhaseProgressCell deal={deal} />
      </TableCell>

      {/* Valor - alinhado à direita */}
      <TableCell className="text-right px-2 py-1.5">
        <PriceDisplay amount={deal.value} currency={deal.currency} size="sm" />
      </TableCell>

      {/* Probabilidade - apenas número/percentual */}
      <TableCell className="text-center text-xs text-muted-foreground px-1 py-1.5">
        {deal.probability || 0}%
      </TableCell>

      {/* Data de criação */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
        {format(new Date(deal.created_at), 'dd/MM/yy', { locale: ptBR })}
      </TableCell>

      {/* Previsão de fechamento */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
        {deal.expected_close_date 
          ? format(new Date(deal.expected_close_date), 'dd/MM/yy', { locale: ptBR })
          : '-'
        }
      </TableCell>

      {/* Data de fechamento real (ganho/perdido) */}
      <TableCell className="text-xs text-muted-foreground px-2 py-1.5">
        {deal.actual_close_date 
          ? format(new Date(deal.actual_close_date), 'dd/MM/yy', { locale: ptBR })
          : '-'
        }
      </TableCell>

      {/* Ação: Abrir deal */}
      <TableCell className="w-6 px-1 py-1.5">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button 
              variant="ghost" 
              size="icon"
              className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                onOpen?.();
              }}
            >
              <ExternalLink className="h-3 w-3" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">
            <p className="text-xs">Abrir negócio</p>
          </TooltipContent>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

/**
 * Aba de negócios do cliente - Layout de tabela premium
 * Com scroll horizontal, ordenação e filtros por status
 */
export function CustomerDealsTab({ 
  customerId, 
  contactId,
  vehicleId,
  vehicleLabel,
  newDealContactId,
  isActive,
  onOpenDeal 
}: CustomerDealsTabProps) {
  // ========== ESTADOS ==========
  
  /** Campo de ordenação ativo */
  const [sortField, setSortField] = useState<SortField>('created_at');
  
  /** Direção da ordenação */
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');
  
  /** Filtro de status ativo */
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  /** v3.3.0: controla o dialog de criação de negócio disparado pelo estado vazio */
  const [newDealOpen, setNewDealOpen] = useState(false);

  /** v3.3.0: permissão de criação de negócios (esconde o botão quando ausente) */
  const { canCreate } = usePermissions();
  const canCreateDeal = canCreate('crm_deals');

  /** v3.3.0: usado para recarregar a lista após criar um negócio */
  const queryClient = useQueryClient();

  // Ref para debug de scroll em DEV - DEVE estar antes de qualquer early return
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  // ========== QUERY ==========
  
  // Busca deals com lazy loading e fallback customer_id → contact_id
  const { data: deals, isLoading, error } = useCustomerDeals(
    customerId,
    contactId,
    { enabled: isActive, vehicleId }
  );

  // ========== PROCESSAMENTO DE DADOS ==========
  
  /**
   * Filtra deals pelo status selecionado
   */
  const filteredDeals = useMemo(() => {
    if (!deals) return [];
    if (statusFilter === 'all') return deals;
    return deals.filter(d => d.status === statusFilter);
  }, [deals, statusFilter]);

  /**
   * Ordena deals pelo campo e direção selecionados
   */
  const sortedDeals = useMemo(() => {
    return [...filteredDeals].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'value':
          comparison = a.value - b.value;
          break;
        case 'status':
          const statusOrder = { open: 0, won: 1, lost: 2 };
          comparison = statusOrder[a.status] - statusOrder[b.status];
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'expected_close_date':
          const dateA = a.expected_close_date ? new Date(a.expected_close_date).getTime() : 0;
          const dateB = b.expected_close_date ? new Date(b.expected_close_date).getTime() : 0;
          comparison = dateA - dateB;
          break;
      }
      
    return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [filteredDeals, sortField, sortDirection]);

  // Debug: verificar se overflow horizontal está funcionando (somente em DEV)
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (container) {
      const hasHorizontalOverflow = container.scrollWidth > container.clientWidth;
      logger.debug('Scroll container métricas', {
        clientWidth: container.clientWidth,
        scrollWidth: container.scrollWidth,
        hasHorizontalOverflow,
        clientHeight: container.clientHeight,
        scrollHeight: container.scrollHeight,
      });
    }
  }, [sortedDeals]);

  // ========== HANDLERS ==========
  
  /**
   * Alterna ordenação: mesmo campo = inverte direção, novo campo = desc
   */
  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // ========== CONTADORES PARA RESUMO ==========
  
  const openDeals = deals?.filter(d => d.status === 'open') || [];
  const wonDeals = deals?.filter(d => d.status === 'won') || [];
  const lostDeals = deals?.filter(d => d.status === 'lost') || [];

  // ========== ESTADOS DE UI ==========

  // Estado de carregamento - skeleton de tabela
  if (isLoading) {
    return (
      <div className="space-y-4">
        {/* Skeleton do resumo e filtros */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-20" />
          </div>
          <div className="flex items-center gap-1">
            <Skeleton className="h-6 w-14" />
            <Skeleton className="h-6 w-16" />
            <Skeleton className="h-6 w-14" />
            <Skeleton className="h-6 w-16" />
          </div>
        </div>
        
        {/* Skeleton da tabela com scroll - usa tabela nativa */}
        <div className="rounded-lg border border-border/50 overflow-hidden">
          <div className="overflow-auto">
            <table className="w-max min-w-[1200px] caption-bottom text-sm table-fixed">
              <TableHeader>
                <TableRow className="bg-muted/20 hover:bg-muted/20 border-b border-border/40">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14].map(i => (
                    <TableHead key={i} className="py-2">
                      <Skeleton className="h-3 w-10" />
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {[1, 2, 3].map((i) => (
                  <TableRow key={i} className="border-b border-border/40">
                    <TableCell className="py-2"><Skeleton className="h-4 w-24" /></TableCell>
                    <TableCell className="py-2"><Skeleton className="h-5 w-12" /></TableCell>
                    <TableCell className="py-2"><Skeleton className="h-4 w-10" /></TableCell>
                    <TableCell className="py-2"><Skeleton className="h-6 w-16" /></TableCell>
                    <TableCell className="py-2"><Skeleton className="h-4 w-14" /></TableCell>
                    <TableCell className="py-2"><Skeleton className="h-4 w-14" /></TableCell>
                    <TableCell className="py-2"><Skeleton className="h-5 w-20" /></TableCell>
                    <TableCell className="py-2"><Skeleton className="h-4 w-14 ml-auto" /></TableCell>
                    <TableCell className="py-2"><Skeleton className="h-3 w-8" /></TableCell>
                    <TableCell className="py-2"><Skeleton className="h-4 w-12" /></TableCell>
                    <TableCell className="py-2"><Skeleton className="h-4 w-12" /></TableCell>
                    <TableCell className="py-2"><Skeleton className="h-4 w-12" /></TableCell>
                    <TableCell className="py-2"><Skeleton className="h-6 w-6" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  // Estado de erro
  if (error) {
    return (
      <div className="rounded-lg border border-border/50 bg-destructive/5 p-6 text-center">
        <Briefcase className="h-8 w-8 mx-auto text-destructive/50 mb-2" />
        <p className="text-sm text-destructive">Erro ao carregar negócios</p>
        <p className="text-xs text-muted-foreground mt-1">
          {(error as Error).message}
        </p>
      </div>
    );
  }

  // v3.6.0: dialog de criação compartilhado entre estado vazio e lista
  const newDealDialog = (
    <>
        {/* Dialog de criação reaproveitado (mesmo usado na sidebar)
            v3.4.0: montado apenas quando aberto — evita carregar catálogo de
            produtos (get_tenant_products_safe) na abertura do modal */}
        {canCreateDeal && newDealOpen && (
          <DealFormDialog
            open={newDealOpen}
            onOpenChange={setNewDealOpen}
            defaultContactId={(newDealContactId ?? contactId) ?? undefined}
            defaultVehicles={vehicleId ? [{ id: vehicleId, label: vehicleLabel || 'Veículo' }] : undefined}
            onSuccess={() => {
              // v3.5.0: o fechamento vem do próprio formulário (após o modal de sucesso)
              // Recarrega a lista da aba e os contadores do modal
              queryClient.invalidateQueries({ queryKey: ['customer-deals', customerId, contactId, vehicleId ?? null] });
              queryClient.invalidateQueries({ queryKey: ['customer-modal-data'] });
            }}
          />
        )}
    </>
  );

  // Sem deals
  if (!deals || deals.length === 0) {
    return (
      <>
        <div className="rounded-lg border border-border/50 bg-muted/20 p-8 text-center">
          <div className="h-12 w-12 rounded-full bg-muted/50 flex items-center justify-center mx-auto mb-3">
            <Briefcase className="h-6 w-6 text-muted-foreground" />
          </div>
          <h4 className="text-sm font-medium mb-1">Nenhum negócio encontrado</h4>
          <p className="text-xs text-muted-foreground">
            Este cliente ainda não possui negócios vinculados.
          </p>

          {/* v3.3.0: atalho de criação — mesmo padrão visual do botão "Novo Negócio"
              das Ações Rápidas da sidebar (ghost + borda tracejada + ícone Plus) */}
          {canCreateDeal && (
            <Button
              variant="ghost"
              size="sm"
              className="mt-4 h-10 px-4 text-xs text-muted-foreground border-2 border-dashed border-muted-foreground/30 rounded-lg hover:border-muted-foreground/50 hover:bg-muted/30 transition-colors"
              onClick={() => setNewDealOpen(true)}
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Novo Negócio
            </Button>
          )}
        </div>

        {newDealDialog}
      </>
    );
  }

  return (
    /**
     * v3.2.0: Container raiz da aba com contenção total de largura
     * - h-full: ocupa altura disponível
     * - min-h-0 + min-w-0: permite encolher (flex shrink)
     * - w-full max-w-full: constrange largura ao container pai
     * - overflow-hidden: clip visual no limite desta aba (não invade sidebar)
     */
    <div className="flex flex-col h-full min-h-0 min-w-0 w-full max-w-full overflow-hidden">
      {newDealDialog}
      {/* ========== HEADER FIXO: Resumo + Filtros ========== */}
      <div className="flex-shrink-0 space-y-2 pb-3">
        {/* Resumo e Filtros */}
        <div className="flex items-center justify-between flex-wrap gap-2">
          {/* Resumo */}
          <div className="flex items-center gap-4 text-xs">
            <span className="text-muted-foreground">
              Total: <strong className="text-foreground">{deals.length}</strong>
            </span>
            <span className="text-emerald-600">
              Ganhos: <strong>{wonDeals.length}</strong>
            </span>
            <span className="text-blue-600">
              Em aberto: <strong>{openDeals.length}</strong>
            </span>
            <span className="text-rose-600">
              Perdidos: <strong>{lostDeals.length}</strong>
            </span>
          </div>

          {/* Filtros por status + v3.6.0 botão Novo Negócio */}
          <div className="flex items-center gap-1">
            {canCreateDeal && (
              <Button variant="outline" size="sm" className="h-6 text-xs px-2 mr-1" onClick={() => setNewDealOpen(true)}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                Novo Negócio
              </Button>
            )}
            <Button
              variant={statusFilter === 'all' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-6 text-xs px-2"
              onClick={() => setStatusFilter('all')}
            >
              Todos
            </Button>
            <Button
              variant={statusFilter === 'open' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-6 text-xs px-2"
              onClick={() => setStatusFilter('open')}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-1" />
              Abertos
            </Button>
            <Button
              variant={statusFilter === 'won' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-6 text-xs px-2"
              onClick={() => setStatusFilter('won')}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1" />
              Ganhos
            </Button>
            <Button
              variant={statusFilter === 'lost' ? 'secondary' : 'ghost'}
              size="sm"
              className="h-6 text-xs px-2"
              onClick={() => setStatusFilter('lost')}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-rose-500 mr-1" />
              Perdidos
            </Button>
          </div>
        </div>

        {/* Indicador de resultados filtrados */}
        {statusFilter !== 'all' && (
          <p className="text-xs text-muted-foreground">
            Exibindo {sortedDeals.length} de {deals.length} negócios
          </p>
        )}
      </div>

      {/* ========== CONTAINER DA TABELA COM SCROLL HORIZONTAL E VERTICAL ========== */}
      {/* 
        ESTRUTURA ROBUSTA PARA SCROLL (padrão WahaSessionsTable):
        1. Container externo: rounded + border + overflow-hidden (clip visual)
        2. Container scroll: overflow-auto + ref para debug
        3. Tabela nativa: min-w grande + w-max (força overflow real)
      */}
      {/* 
        v1.2.0: Container de scroll com largura constrangida
        - w-full + max-w-full: força o container a respeitar limites do pai
        - min-w-0: permite encolher além do conteúdo natural
        - rounded + border + overflow-hidden: clip visual nas bordas
      */}
      <div className="flex-1 min-h-0 w-full max-w-full min-w-0 rounded-lg border border-border/50 overflow-hidden">
        {/* 
          v1.2.0: Container de scroll - controle único de overflow
          - w-full + max-w-full: constrange a largura visível
          - overflow-auto: habilita scroll X e Y quando necessário
        */}
        <div 
          ref={scrollContainerRef}
          className="h-full w-full max-w-full overflow-auto"
        >
          {/* 
            TABELA NATIVA: NÃO usa componente <Table> do shadcn
            O componente Table injeta wrapper com overflow-auto próprio
            que conflita com nosso controle externo.
            Usamos <table> nativo com mesmas classes do shadcn.
            
            v1.2.0: min-w aumentado para 1600px para garantir overflow
            em telas menores (1366px, notebooks, etc.)
          */}
          <table className="w-max min-w-[1080px] caption-bottom text-sm table-fixed">
            {/* Definição explícita de larguras das colunas - compactas */}
            <colgroup>
              <col className="w-[8px]" />    {/* Barra de status */}
              <col className="w-[75px]" />    {/* N.Negócio */}
              <col className="w-[110px]" />   {/* Título */}
              <col className="w-[65px]" />    {/* Status */}
              <col className="w-[65px]" />    {/* Prioridade */}
              <col className="w-[75px]" />    {/* Vendedor */}
              <col className="w-[75px]" />    {/* Workspace */}
              <col className="w-[75px]" />    {/* Pipeline */}
              <col className="w-[100px]" />   {/* Fase */}
              <col className="w-[80px]" />    {/* Valor */}
              <col className="w-[45px]" />    {/* Prob. */}
              <col className="w-[65px]" />    {/* Criado */}
              <col className="w-[70px]" />    {/* Prev. Fech. */}
              <col className="w-[65px]" />    {/* Fechado */}
              <col className="w-[36px]" />    {/* Ação */}
            </colgroup>
            {/* Header sticky para não perder contexto ao rolar */}
            <TableHeader className="sticky top-0 bg-background z-[1]">
              <TableRow className="bg-muted/20 hover:bg-muted/20 border-b border-border/40">
                <TableHead className="w-2 p-0" /> {/* Barra de status */}
                <TableHead className="text-xs font-medium px-2 py-1.5">N.Negócio</TableHead>
                <SortableHeader 
                  field="title" 
                  label="Título" 
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader
                  field="status" 
                  label="Status" 
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <TableHead className="text-xs font-medium px-2 py-1.5">Prioridade</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1.5">Vendedor</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1.5">Workspace</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1.5">Pipeline</TableHead>
                <TableHead className="text-xs font-medium px-2 py-1.5">Fase</TableHead>
                <SortableHeader 
                  field="value" 
                  label="Valor" 
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                  align="right"
                />
                <TableHead className="text-xs font-medium text-center px-1 py-1.5">Prob.</TableHead>
                <SortableHeader 
                  field="created_at" 
                  label="Criado" 
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <SortableHeader 
                  field="expected_close_date" 
                  label="Prev. Fech." 
                  currentField={sortField}
                  direction={sortDirection}
                  onSort={handleSort}
                />
                <TableHead className="text-xs font-medium px-2 py-1.5">Fechado</TableHead>
                <TableHead className="w-6 px-1 py-1.5" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedDeals.map((deal) => (
                <DealTableRow 
                  key={deal.id} 
                  deal={deal} 
                  onOpen={() => onOpenDeal?.(deal.id)}
                />
              ))}
            </TableBody>
          </table>
        </div>
      </div>
    </div>
  );
}
